import { createFileRoute, Link } from "@tanstack/react-router";
import { ALL_LESSONS, MODULES } from "@/data/lessons";
import {
  AccountShell,
  BackLink,
  PageHeader,
  PaperRule,
  mono,
  serif,
} from "@/components/AccountLayout";
import { useCompletions } from "@/hooks/useCompletions";

export const Route = createFileRoute("/account_/progress")({
  head: () => {
    const title = "Your progress — WHO KNEW";
    const description =
      "Manage your WHO KNEW account, view your purchase, track your progress, or get in touch.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/account/progress" },
      ],
    };
  },
  component: ProgressPage,
});

function ProgressPage() {
  const completed = useCompletions();

  const total = ALL_LESSONS.length;
  const done = ALL_LESSONS.reduce((n, l) => n + (completed.has(l.id) ? 1 : 0), 0);
  const isNone = done === 0;
  const isAll = done === total;


  const bigLine = isNone
    ? "Not started yet."
    : isAll
      ? "Course complete."
      : `${done} of 29 lessons complete.`;

  const subline = isNone
    ? "Saturday afternoon is calling."
    : isAll
      ? "All 29 lessons. Done. Quietly impressive."
      : "You're in the middle of it. Keep going.";

  const nextLesson = ALL_LESSONS.find((l) => !completed.has(l.id)) ?? ALL_LESSONS[0];
  const linkLabel = isNone
    ? "Start the course"
    : isAll
      ? "Revisit a lesson"
      : "Continue where you left off";

  return (
    <AccountShell>
      <BackLink />
      <PageHeader eyebrow="YOUR PROGRESS" title="Where you are in the course." />

      <div style={{ marginTop: "48px" }}>
        <p
          className="text-ink font-bold"
          style={{
            ...serif,
            fontSize: "24px",
            lineHeight: 1.2,
            fontWeight: 700,
          }}
        >
          <span className="md:hidden">{bigLine}</span>
          <span className="hidden md:inline" style={{ fontSize: "32px" }}>
            {bigLine}
          </span>
        </p>

        <p
          className="italic"
          style={{ ...serif, fontSize: "18px", color: "#5A534A", marginTop: "24px" }}
        >
          {subline}
        </p>

        <div style={{ marginTop: "24px" }}>
          {isAll ? (
            <Link
              to="/my-courses"
              className="italic text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2"
              style={{ ...serif, fontSize: "18px" }}
            >
              {linkLabel}
            </Link>
          ) : (
            <Link
              to="/courses/business-and-accountancy/basic-business-accountancy/lessons/$lessonId"
              params={{ lessonId: isNone ? "1.1" : nextLesson.id }}
              className="italic text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2"
              style={{ ...serif, fontSize: "18px" }}
            >
              {linkLabel}
            </Link>
          )}
        </div>
      </div>

      <PaperRule />

      <section style={{ paddingBottom: "96px" }}>
        <p
          className="font-bold uppercase tracking-[0.05em] text-burgundy"
          style={{ ...mono, fontSize: "11px" }}
        >
          BY MODULE
        </p>

        <div style={{ marginTop: "16px" }}>
          {MODULES.map((m, idx) => {
            const totalLessons = m.lessons.length;
            const doneLessons = m.lessons.reduce(
              (n, l) => n + (completed.has(l.id) ? 1 : 0),
              0,
            );
            const isModuleDone = doneLessons === totalLessons;
            const numLabel = String(m.number).padStart(2, "0");
            return (
              <div key={m.number}>
                {idx > 0 && <div style={{ height: "1px", backgroundColor: "#E8E1D3" }} />}
                <div
                  className="flex items-center justify-between gap-4"
                  style={{ padding: "12px 0" }}
                >
                  <p
                    className="font-bold uppercase tracking-[0.05em] text-burgundy"
                    style={{ ...mono, fontSize: "12px" }}
                  >
                    MODULE {numLabel} — {m.title}
                  </p>
                  <p
                    className="font-bold uppercase tracking-[0.05em] text-right"
                    style={{
                      ...mono,
                      fontSize: "10px",
                      color: isModuleDone ? "#8B1E1E" : "#5A534A",
                    }}
                  >
                    {doneLessons} OF {totalLessons} LESSONS
                  </p>
                </div>
              </div>
            );
          })}
          <div style={{ height: "1px", backgroundColor: "#E8E1D3" }} />
        </div>
      </section>
    </AccountShell>
  );
}
